// When the phone page plays its earcons (../earcons.ts), so the user can tell by ear when the mic
// is really listening. The page reports what happens; this decides which cue, if any:
// - live(): the mic is really open (the session started, or the mic opened in it, after the
//   capture heard audio) -> "listening", with the mic muted to the kit while it plays;
// - closed(): the session ended or the mic stopped or paused -> "stopped", once, and only if a
//   "listening" cue opened it;
// - a reply heard to the end and the kit listening again, the mic still open -> "turn" (quiet,
//   the mic muted to the kit for it too); a reply silenced or barged in on plays nothing;
// - needsTap(): the mic stopped on its own and the page is visible -> "tap". Not on return from
//   the background: iOS plays nothing until a tap, and the player won't queue a cue for later.

import type { CueKind } from "../earcons";
import type { KitState } from "../protocol";

export const CUES_PREF = "lv-sound-cues";

/** The "Sound cues" setting (default on), stored like the page's other settings. */
export function loadCuesPref(storage: Pick<Storage, "getItem"> | null = safeStorage()): boolean {
  try {
    return storage?.getItem(CUES_PREF) !== "off";
  } catch {
    return true;
  }
}

export function saveCuesPref(on: boolean, storage: Pick<Storage, "setItem"> | null = safeStorage()): void {
  try {
    storage?.setItem(CUES_PREF, on ? "on" : "off");
  } catch {
    /* private mode */
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** Extra time the mic stays muted to the kit after the "listening" cue ends (room tail). */
export const MUTE_TAIL_MS = 120;

export class SoundCues {
  enabled: boolean;
  private open = false;
  private kit: KitState = "idle";
  private turnPending = false;
  /** Whether the page's mic is capturing right now; "turn" never plays while it isn't. */
  micLive: () => boolean = () => true;

  /**
   * `play` plays a cue and returns its length in ms (0 if it couldn't play); `mute` keeps the
   * mic's frames from the kit for that long.
   */
  constructor(
    private readonly play: (kind: CueKind) => number,
    private readonly mute: (ms: number) => void,
    enabled = true,
  ) {
    this.enabled = enabled;
  }

  /** The mic is really live and sending: "listening". */
  live(): void {
    if (this.open) return;
    this.open = true;
    this.turnPending = false;
    const ms = this.cue("listening");
    if (ms) this.mute(ms + MUTE_TAIL_MS);
  }

  /** The session ended, or the mic was stopped or paused: "stopped" (once). */
  closed(): void {
    if (!this.open) return;
    this.open = false;
    this.turnPending = false;
    this.cue("stopped");
  }

  /** The mic stopped on its own and needs a tap to come back: "tap" (instead of "stopped"). */
  needsTap(): void {
    this.open = false;
    this.turnPending = false;
    this.cue("tap");
  }

  /** The kit's state changed. */
  state(s: KitState): void {
    this.kit = s;
    if (s === "listening") {
      if (this.turnPending) this.yourTurn();
    } else if (s !== "speaking") this.turnPending = false;
  }

  /** A reply's audio is done here: heard to the end, the user's turn comes next. */
  heard(outcome: string): void {
    if (outcome !== "finished" || !this.open || !this.micLive()) return void (this.turnPending = false);
    if (this.kit === "listening") this.yourTurn();
    else this.turnPending = true;
  }

  get listening(): boolean {
    return this.open;
  }

  private yourTurn(): void {
    this.turnPending = false;
    if (!this.open || !this.micLive()) return;
    const ms = this.cue("turn");
    if (ms) this.mute(ms + MUTE_TAIL_MS); // the user speaks after the tick, not over it
  }

  private cue(kind: CueKind): number {
    return this.enabled ? this.play(kind) : 0;
  }
}
